<template>
	<form>
		<div class="flex">
			<div class="w-full form-group">
				<BaseSelect
					ref="BaseSelect"
					:required="required"
					@input="prefill($event)"
					:label="optionsLabel"
					:options="dataOptions"
					v-model="selectedOption"
					:allow-empty="allowEmpty"
					v-if="showSelect"
					:disabled="disabled"
					:disable-search="false"
				/>
			</div>
		</div>

		<div v-if="selectedOption || !showSelect">
			<div v-show="isCustomSet">
				<slot name="warning"></slot>
			</div>

			<div class="flex flex-wrap">
				<BaseInput
					ref="BaseInput.company.name"
					:required="true"
					class="w-full"
					:label="$t('name')"
					type="text"
					v-model="company.name"
					:disabled="!isCustomSet"
					@validationComplete="checkValidation('name')"
				/>
			</div>
			<div class="flex" v-if="showPostalOptions">
				<CleanBaseRadio
					v-for="option in postalOptions"
					:value="option.value"
					:label="option.display"
					:form-value="selectedPostalOption"
					:key="option.display"
					@click="setSelectedPostal($event)"
					:class="option.class"
					class="pt-2 pb-4"
					:disabled="!isCustomSet"
				/>
			</div>

			<div class="flex" v-if="!showPostalOptions || selectedPostalOption === postalOptions[0].value">
				<BaseInput
					ref="BaseInput.company.street"
					:required="true"
					class="w-full md:w-1/2 pr-0 md:pr-2"
					:label="$t('street')"
					:placeholder="$t('street_postal_box')"
					type="text"
					v-model="company.street"
					:disabled="!isCustomSet"
					@validationComplete="checkValidation('street')"
				/>

				<BaseInput
					ref="BaseInput.company.houseNumber"
					class="w-full md:w-1/2 md:pl-2"
					:label="$t('number')"
					:placeholder="$t('number')"
					:required="false"
					type="tel"
					v-model="company.houseNumber"
					:disabled="!isCustomSet"
					@validationComplete="checkValidation('houseNumber')"
				/>
			</div>
			<div class="flex" v-else>
				<BaseInput
					:label="$t('po_box')"
					ref="BaseInput.company.poBox"
					:required="true"
					type="text"
					v-model="company.poBox"
					class="w-full mr-4"
					:disabled="!isCustomSet"
				/>
			</div>

			<CityInput
				ref="BaseInput-zip"
				v-model="company.city"
				:disabled="!isCustomSet"
				@validationComplete="checkValidation('city_special')"
				:zip-code-required="zipCodeRequired"
			/>

			<div class="flex flex-wrap">
				<BaseInput
					ref="BaseInput.company.phoneNumber"
					:required="true"
					class="w-full md:w-1/2 pr-0 md:pr-2"
					:label="$t('phone')"
					v-model="company.phoneNumber"
					type="tel"
					validationtype="phone"
					:disabled="!isCustomSet"
					@validationComplete="checkValidation('phoneNumber')"
				/>
				<BaseInput
					:required="true"
					ref="BaseInput.company.email"
					validationtype="email"
					class="w-full md:w-1/2 pl-0 md:pl-2"
					:label="$t('e_mail')"
					v-model="company.email"
					:error-message="$t('e_mail_not_valid')"
					type="email"
					:disabled="!isCustomSet"
					@validationComplete="checkValidation('email')"
				/>
			</div>
		</div>
	</form>
</template>

<script>
import BaseInput from '../../forms/inputs/BaseInput';
import { _regxp } from '@/plugins/Utils';
import BaseSelect from '@/components/forms/inputs/BaseSelect';
import { LocationsApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import Validate from '@/mixins/Validate';
import CityInput from '@/components/forms/CityInput';
import { isNumeric } from '@/helpers/Utils';
import CleanBaseRadio from '@/components/forms/inputs/CleanBaseRadio.vue';

export default {
	components: { CleanBaseRadio, BaseSelect, BaseInput, CityInput },
	name: 'CompanyForm',
	mixins: [Validate],
	props: {
		customLabel: { type: String },
		options: { type: Array, required: true },
		optionsLabel: { type: String, required: true },
		value: {},
		required: { default: true },
		allowEmpty: { default: false },
		showSelect: { default: true },
		highlight_suffix: {},
		disabled: { default: false },
		showPostalOptions: { default: false, required: false },
	},
	data() {
		return {
			dataOptions: [],
			regxp: _regxp(),
			selectedOption: null,
			validIgnored: [],
			postalOptions: [
				{
					display: 'Straße',
					value: 0,
				},
				{
					display: 'Postfach',
					value: 1,
				},
			],
			selectedPostalOption: 0,
		};
	},
	mounted() {
		this.company.poBox === null || this.company.poBox === undefined ? this.setSelectedPostal(0) : this.setSelectedPostal(1);
	},
	watch: {
		company: {
			handler() {
				if (this.company.name) {
					this.company.display = this.company.isEditable ? this.customLabel : this.company.name;
					this.selectedOption = this.company;
				}
			},
		},
		options: {
			deep: true,
			immediate: true,
			handler() {
				this.dataOptions = [];
				this.options.forEach(option => {
					option.display = option.name;
					this.dataOptions.push(option);
				});
				const customCompany = this.company?.isEditable ? this.company : null;
				if (this.customLabel && !customCompany) {
					this.dataOptions.unshift({
						id: null,
						name: null,
						display: this.customLabel,
						highlight: true,
						houseNumber: null,
						street: null,
						phoneNumber: null,
						email: null,
						city: {
							zipCode: null,
							name: null,
						},
						isEditable: true,
						highlight_suffix: this.highlight_suffix || null,
					});
				} else {
					if (customCompany && customCompany.display) {
						customCompany.display = this.customLabel;
						this.dataOptions.unshift(customCompany);
					}
				}

				if (this.company.name) {
					this.company.display = this.company.isEditable ? this.customLabel : this.company.name;
					this.selectedOption = this.company;
				}
			},
		},
	},
	computed: {
		zipCodeRequired() {
			// if street is Postleitzahl
			return !this.company.street || !isNumeric(this.company.street);
		},
		customSelected() {
			return this.value?.display === this.customLabel;
		},
		isCustomSet: function() {
			return (this.company && this.company.isEditable) || !this.showSelect;
		},
		company: {
			get() {
				if (this.value && this.value.city) {
					return this.value;
				} else {
					return { city: { zipCode: null } };
				}
			},
			set(v) {
				this.$emit('input', v);
			},
		},
	},
	methods: {
		setSelectedPostal(event) {
			this.selectedPostalOption = event;
		},
		checkValidation(field) {
			if (this.customSelected) {
				if (this.validIgnored.indexOf(field) === -1) {
					if (field === 'city_special') {
						this.$refs['BaseInput-zip'].setTempValid();
						this.validIgnored.push(field);
					} else {
						this.$refs['BaseInput.company.' + field].isValid = true;
						this.validIgnored.push(field);
					}
				}
			}
		},
		prefill($event) {
			this.selectedOption = $event;
			if ($event) {
				this.company = {
					...this.selectedOption,
					name: this.selectedOption.name,
					city: {
						name: this.selectedOption.city.name,
						zipCode: this.selectedOption.city.zipCode || '',
					},
				};
			} else {
				this.company = {};
			}

			if (this.customSelected) {
				this.validIgnored = [];
			}
		},
		zipCodeChanged: function() {
			if (this.company.city.zipCode.toString().length === 5) {
				this.getCityToZipCode();
			} else {
				this.company.city.name = null;
			}
		},
		getCityToZipCode: function() {
			LocationsApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.locationsCitiesGet(this.company.city.zipCode)
				.then(response => {
					this.company.city = { ...response.data };
				})
				.catch(error => {
					//set name and id to null to remove city in ui
					this.company.city.name = null;
					this.company.city.id = null;
					this.$refs['BaseInput.company.city.zipcode'].isValid = false;
				});
		},
	},
};
</script>

<style lang="sass" scoped></style>
